import { buttonFunctions } from "hec/builder/blocks/components/cta/cta.helper";
import { BuilderLinkWrapper } from "hec/builder/blocks/components/cta/linkWrapper";

import {
  builderImageSrcSet,
  builderImageUrl,
  builderImageWidths,
} from "~/hec/utils/builder-image-url";
import { cn } from "~/lib/utils";

import type { ImageTextItem } from "./types";

/**
 * One image + text card. Wraps its content in the shared `BuilderLinkWrapper`
 * so the link supports a Builder page reference OR a custom URL (internal SPA
 * nav / external / SFRA full-load) — parity with the live homepage tiles and
 * inherits the LinkWrapper route-resolution fixes (SFCC-11453 / SFCC-11843).
 */
const ImageTextCarouselItem: React.FC<ImageTextItem> = ({
  image,
  text,
  isInternalLink = true,
  pageReference,
  externalLinkReference,
  customUrl,
  customUrlIsSfra,
  openInNewTab,
  badge,
  badgeBackgroundColor,
}) => {
  // Same model as the Link block's badge: an author-picked color replaces the
  // class-based default (the per-site `--tile-badge-bg`); blank keeps it.
  const badgeText = badge?.trim();
  const customBadgeColor = badgeBackgroundColor?.trim() || undefined;
  return (
    <div
      data-testid="image-text-carousel-item"
      className="image-text-carousel-item relative mx-auto max-w-[120px] text-center lg:max-w-[140px] xl:min-w-[130px]"
    >
      {/* Link is a LinkWrapper → Button, which stamps its own
          data-testid="button-Button" on the anchor; a testid passed here is
          dropped, so the per-item testid lives on this container instead. */}
      <BuilderLinkWrapper
        clickFunction={buttonFunctions.linkToPage}
        isInternalLink={isInternalLink}
        pageReference={pageReference}
        externalLinkReference={externalLinkReference}
        customUrl={customUrl}
        customUrlIsSfra={customUrlIsSfra}
        openInNewTab={openInNewTab}
        attributes={{ className: "gap-xs flex flex-col items-center" }}
      >
        {image && (
          <img
            src={builderImageUrl(image, { width: 180, format: "webp" })}
            srcSet={builderImageSrcSet(image, builderImageWidths(90))}
            sizes="90px"
            alt={text ? text : "Image Text Carousel Item"}
            className={cn(
              "image-text-carousel-image object-contain",
              "h-[70px] w-[70px] lg:h-[70px] lg:w-[70px] xl:h-[90px] xl:w-[90px]",
            )}
            loading="lazy"
          />
        )}
        {text && (
          <p className="image-text-carousel-text text-wrap pb-[2px] text-[12px] font-medium leading-none lg:text-[14px]">
            {text}
          </p>
        )}
        {/* Inside the link so it is read as part of the link text. */}
        {badgeText && (
          <span
            className={cn(
              "image-text-carousel-badge absolute right-[-4px] top-0 whitespace-nowrap rounded-[20px] pb-[4px] pl-[9px] pr-[6px] pt-[6px] text-[14px] font-semibold leading-none text-white sm:right-[4px]",
              !customBadgeColor && "bg-[hsl(var(--tile-badge-bg))]",
            )}
            style={
              customBadgeColor
                ? { backgroundColor: customBadgeColor }
                : undefined
            }
            data-testid="image-text-carousel-item-badge"
          >
            {badgeText}
          </span>
        )}
      </BuilderLinkWrapper>
    </div>
  );
};

export default ImageTextCarouselItem;
